import React, { useState, useEffect, useCallback } from "react";
import { Button, message, Tag, Tooltip } from "antd";
import {
  PlusOutlined,
  FileSearchOutlined,
  ArrowLeftOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import TableContainer from "../../components/TableContainer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getHubReconciliationList,
  startHubReconciliation,
  clearStartHubReconciliation,
} from "../../actionsMethods/hubReconciliationMethods";
import { getToken, getPremiseId } from "../../utils/worker";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import { getApiMessage } from "./apiMessage";
import "./styles.scss";

const getSelectedHubInfo = () => {
  try {
    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
    if (activeHub?.hubId) {
      return {
        hubId: activeHub.hubId,
        hubName: activeHub.hubName || activeHub.premiseName || "Selected Hub",
      };
    }
  } catch (e) {
    console.error("Error parsing active_hub", e);
  }

  try {
    const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
    return {
      hubId: userInfoData?.userInfo?.premiseId,
      hubName:
        userInfoData?.premises?.premiseName ||
        userInfoData?.userInfo?.premiseName ||
        "Hub",
    };
  } catch (e) {
    return { hubId: null, hubName: "Hub" };
  }
};

const HubReconciliation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedHub, setSelectedHub] = useState(getSelectedHubInfo);

  const {
    fetching: isFetching,
    result: listData,
  } = useSelector((state) => state.hubReconciliationReducer.getHubReconciliationList);

  const {
    result: startData,
    error: startError,
  } = useSelector((state) => state.hubReconciliationReducer.startHubReconciliation);

  const fetchReconciliationData = useCallback((page = 1, pageSize = 10) => {
    const tokenStr = getToken();
    const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

    const premiseIdStr = getPremiseId();
    const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;

    if (!premiseId || premiseId === "undefined" || premiseId === "null") {
      setData([]);
      setPagination({ current: 1, pageSize, total: 0 });
      message.warning("Please select a hub from the header to view reconciliation data", 3);
      return;
    }

    dispatch(getHubReconciliationList({ page, pageSize, token, premiseId }));
  }, [dispatch]);

  useEffect(() => {
    if (listData) {
      const responseData = listData.data || listData || {};
      let extractedData = [];
      if (Array.isArray(responseData.data)) {
        extractedData = responseData.data;
      } else if (Array.isArray(responseData)) {
        extractedData = responseData;
      } else if (Array.isArray(listData)) {
        extractedData = listData;
      }

      setData(extractedData);
      setPagination({
        current: (responseData.pageNum || 0) + 1,
        pageSize: responseData.pageSize || 10,
        total: responseData.totalCount || 0,
      });
    }
  }, [listData]);

  const handleStartReconciliation = () => {
    const tokenStr = getToken();
    const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

    const premiseIdStr = getPremiseId();
    const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;

    if (!premiseId || premiseId === "undefined" || premiseId === "null") {
      message.warning("Please select a hub from the header before starting reconciliation", 3);
      return;
    }

    const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
    const userId = userInfoData?.userInfo?.userId ? userInfoData.userInfo.userId.toString() : "";
    const hubInfo = getSelectedHubInfo();
    const premiseName = hubInfo.hubName || "Hub";

    dispatch(startHubReconciliation({ token, premiseId, userId, premiseName }));
  };

  useEffect(() => {
    if (startData) {
      const reconcileId = startData?.data?.reconcileId || startData?.reconcileId;
      if (reconcileId) {
        message.success(getApiMessage(startData, "Hub reconciliation started successfully"), 2);
        window.open(`/start-reconciliation?reconcileId=${reconcileId}`, "_blank");
        dispatch(clearStartHubReconciliation());
      }
    } else if (startError) {
      message.error(getApiMessage(startError, "Failed to start reconciliation"), 3);
      dispatch(clearStartHubReconciliation());
    }
  }, [startData, startError, dispatch]);

  useEffect(() => {
    dispatch(clearStartHubReconciliation());
    setSelectedHub(getSelectedHubInfo());
    fetchReconciliationData();

    const handleHubChange = () => {
      setSelectedHub(getSelectedHubInfo());
      fetchReconciliationData(1, 10);
    };

    window.addEventListener("hubChanged", handleHubChange);
    return () => window.removeEventListener("hubChanged", handleHubChange);
  }, [dispatch, fetchReconciliationData]);

  const handleTableChange = (newPagination) => {
    fetchReconciliationData(newPagination.current, newPagination.pageSize);
  };

  const columns = [
    {
      title: "Reconcile ID",
      dataIndex: "reconcileId",
      key: "reconcileId",
      align: "center",
      width: 130,
      render: (text, record) => {
        if (record.status === "IN_PROGRESS") {
          return (
            <Tooltip title="Open scanning screen">
              <span
                style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline", fontWeight: 600 }}
                onClick={() => window.open(`/start-reconciliation?reconcileId=${record.reconcileId}`, "_blank")}
              >
                {text}
              </span>
            </Tooltip>
          );
        }
        return <span style={{ fontWeight: 500 }}>{text}</span>;
      },
    },
    { title: "Started At", dataIndex: "startedAt", key: "startedAt", align: "center" },
    {
      title: "Closed At",
      dataIndex: "closedAt",
      key: "closedAt",
      align: "center",
      render: (text) => text || "—",
    },
    { title: "Started By", dataIndex: "startedBy", key: "startedBy", align: "center" },
    { title: "Premise Name", dataIndex: "premiseName", key: "premiseName", align: "center" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        if (status === "CLOSED") {
          return <Tag color="error">Closed</Tag>;
        }
        if (status === "IN_PROGRESS") {
          return <Tag color="warning">In Progress</Tag>;
        }
        return <Tag>{status}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 160,
      render: (_, record) => {
        if (record.status === "IN_PROGRESS") {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip title="Continue scanning for this session">
                <Button
                  type="primary"
                  className="save-btn hub-recon__action-btn"
                  icon={<ScanOutlined />}
                  onClick={() =>
                    window.open(`/start-reconciliation?reconcileId=${record.reconcileId}`, "_blank")
                  }
                >
                  Continue
                </Button>
              </Tooltip>
            </div>
          );
        }

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="View shipment report">
              <Button
                type="primary"
                className="save-btn hub-recon__action-btn"
                icon={<FileSearchOutlined />}
                onClick={() => navigate(`/hub-reconciliation-report?reconcileId=${record.reconcileId}`)}
              >
                Report
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div className="dashboard-container hub-recon" style={{ padding: "20px" }}>
      <div className="hub-recon__header">
        <div>
          <div className="hub-recon__title">
            <ArrowLeftOutlined
              className="hub-recon__back"
              onClick={() => navigate(-1)}
            />
            Hub Reconciliation
          </div>
          <div className="hub-recon__subtitle">
            Showing data for: <strong>{selectedHub.hubName || "Select a hub"}</strong>
            {" · "}
            Use Select Hub in the header to switch hubs
          </div>
        </div>
      </div>

      <div className="master-main-container view-all-staff">
        <div className="hub-recon__toolbar">
          <Button
            type="primary"
            className="save-btn hub-recon__btn"
            icon={<PlusOutlined />}
            onClick={handleStartReconciliation}
          >
            Start Reconciliation
          </Button>
        </div>
        <div className="hub-recon__table-wrap">
          <TableContainer
            className="table-head-sticky"
            dataSource={data}
            columns={columns}
            rowKey="reconcileId"
            loading={{
              indicator: (
                <img
                  src={Smcs_Loader}
                  style={{ height: "100px", width: "100px" }}
                  alt="loading"
                />
              ),
              spinning: isFetching,
            }}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} sessions`,
            }}
            onChange={handleTableChange}
            scroll={{ x: true }}
          />
        </div>
      </div>
    </div>
  );
};

export default HubReconciliation;
